import { gql } from '@apollo/client/core';
import { minutesToMilliseconds, secondsToMilliseconds } from 'date-fns';
import { ChangeEvent, useCallback, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router';
import { Redirect, useLocation } from 'react-router-dom';
import tw, { css, styled } from 'twin.macro';
import 'styled-components/macro';

import { LoadingImage } from 'component-lib/atoms/LoadingImage/LoadingImage';
import { Assignment } from 'play/organisms/assignment/Assignment';
import { IdleTracker } from 'play/organisms/idle-tracker/IdleTracker';
import { LoadingModal } from 'play/organisms/progress-modal/LoadingModal';
import { SkipModal } from 'play/organisms/progress-modal/SkipModal';
import { Workspace } from 'play/organisms/workspace/Workspace';
import { AssignmentPosition } from 'play/support/assignmentPosition';
import { getSidebarWidth } from 'play/support/getSidebarWidth';
import { isSkippingEnabled } from 'play/support/isSkippingEnabled';
import { useSkilljar } from 'play/support/skilljar';
import { useSkipToChallenge } from 'play/support/skipToChallenge';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';

import { useNotificationContext } from '../../../core/context/Notifications';
import ErrorMutation from '../../../core/legacy/components/core/Error';
import { createPathPrefix } from '../../../support/location';
import { useToggle } from '../../../support/react/hooks';
import {
  ChallengeStatus,
  ChallengeTabType,
  ChallengeType,
  CheckChallengeMutation,
  CheckQuizMutation,
  LearnEnvironmentInviteQuery,
  LearnEnvironmentQuery,
  Mode,
  TrackStatus,
  useCheckChallengeMutation,
  useCheckQuizMutation,
  useLearnEnvironmentInviteQuery,
  useLearnEnvironmentKeepAliveMutation,
  useLearnEnvironmentQuery,
  useLearnEnvironmentStopTrackMutation,
} from '../../../types/graphql';
import {
  AssignmentSidebar,
  DEFAULT_SIDEBAR_OVERLAY,
  DEFAULT_SIDEBAR_POSITION,
} from '../../atoms/assignment-sidebar/AssignmentSidebar';
import { LabMetadataContext } from '../../context/LabMetadataContext';
import NotesOverlay from '../../legacy/notes/overlay';
import { TrackNotFound } from '../../organisms/track-not-found/TrackNotFound';
import embedEvent from '../../support/embedEvent';
import DarkOverlay from '../../templates/dark-overlay/DarkOverlay';
export interface LearnEnvironmentProps {
  mode: Mode;
  teamSlug: string;
  trackSlug: string;
  challengeSlug: string;
  inviteID?: string;
  isPreview?: boolean;
}

export function LearnEnvironment({
  mode,
  teamSlug,
  trackSlug,
  challengeSlug,
  inviteID,
  isPreview,
}: LearnEnvironmentProps) {
  const { search } = useLocation();
  const learnEnvironmentSearchParams = new URLSearchParams(search);
  const userID = learnEnvironmentSearchParams.get('userID');
  const searchInviteID = learnEnvironmentSearchParams.get('inviteID');
  const [invalidStateOnLoad, setInvalidStateOnLoad] = useState<boolean | null>(null);

  const invite = inviteID || searchInviteID || null;

  const { data, loading, error } = useLearnEnvironmentQuery({
    fetchPolicy: 'network-only',
    variables: { teamSlug, trackSlug, challengeSlug, userID, inviteID: invite },

    /**
     * We check if initially the track/challenge was in an invalid state on load.
     * If so, we redirect later.
     *
     * If we enter an invalid state later due to other reasons (e.g. track auto-stopped)
     * We do not redirect.
     */
    onCompleted({ track, challenge }) {
      if (typeof invalidStateOnLoad === 'boolean' || !track || !challenge) {
        return;
      }

      const trackInvalid = [
        TrackStatus.Creating,
        TrackStatus.Stopped,
        TrackStatus.Cleaned,
        TrackStatus.Failed,
      ].includes(track.status!);
      const challengeInvalid = challenge.status === ChallengeStatus.Locked;

      setInvalidStateOnLoad(trackInvalid || challengeInvalid);
    },
  });

  const { data: inviteData } = useLearnEnvironmentInviteQuery({
    variables: { inviteID: invite as string },
    skip: Boolean(!invite),
  });

  const { track, challenge, team } = data || {};
  const { trackInvite } = inviteData || {};

  if (loading) {
    return (
      <DarkOverlay>
        <LoadingImage />
      </DarkOverlay>
    );
  }

  if (error) {
    if (error.message.includes('Entity not found')) {
      return <TrackNotFound trackSlug={trackSlug} teamSlug={teamSlug} />;
    }

    return (
      <DarkOverlay>
        <ErrorMutation type="error" title="Error" message={error.message} />
      </DarkOverlay>
    );
  }

  if (invalidStateOnLoad) {
    return <Redirect to={`${createPathPrefix({ mode, inviteID })}/${teamSlug}/tracks/${trackSlug}`} />;
  }

  const skippingEnabled = isSkippingEnabled(data);

  return track && challenge && team ? (
    <LabMetadataContext
      participantId={track.participant?.id}
      trackId={track.id}
      trackSkippable={skippingEnabled}
      inviteId={inviteID}
      teamSlug={teamSlug}
      mode={mode}
    >
      <Body
        isPreview={isPreview}
        mode={mode}
        inviteID={invite}
        userID={userID}
        skippingEnabled={skippingEnabled}
        team={team}
        track={track}
        challenge={challenge}
        challenges={track.challenges}
        invite={trackInvite}
      />
    </LabMetadataContext>
  ) : null;
}

export interface BodyProps {
  mode: Mode;
  skippingEnabled: boolean;
  track: NonNullable<LearnEnvironmentQuery['track']>;
  challenge: NonNullable<LearnEnvironmentQuery['challenge']>;
  challenges: NonNullable<LearnEnvironmentQuery['track']>['challenges'];
  team: NonNullable<LearnEnvironmentQuery['team']>;
  isPreview?: boolean;
  notesPreview?: boolean;
  inviteID?: string | null;
  userID?: string | null;
  invite?: LearnEnvironmentInviteQuery['trackInvite'];
}

export function Body({
  mode,
  inviteID = null,
  userID = null,
  skippingEnabled,
  track,
  challenge,
  challenges,
  team,
  invite,
  isPreview,
  notesPreview,
}: BodyProps) {
  const [isResizing, setIsResizing] = useState(false);
  const [showNotes, { toggle: toggleNotes }] = useToggle(notesPreview ?? false);
  const [sidebarWidth, setSidebarWidth] = useState(() => getSidebarWidth(track.lab_config?.width));
  const [sidebarPosition, setSidebarPosition] = useState<AssignmentPosition>(
    track.lab_config?.position === 'left' ? 'left' : DEFAULT_SIDEBAR_POSITION,
  );
  const [sidebarOverlay] = useState(track.lab_config?.overlay ?? DEFAULT_SIDEBAR_OVERLAY);
  const [sidebarCollapsed, { set: setSidebarCollapsed }] = useToggle(false);
  const [stopTrackMutation, { called: stopTrackCalled }] = useLearnEnvironmentStopTrackMutation();
  const [keepAliveMutation] = useLearnEnvironmentKeepAliveMutation({ variables: { trackId: track.id } });

  const onTrackComplete = () => {
    embedEvent('track.completed', { track_slug: track.slug, total_challenges: totalChallenges });
    trackAmplitudeEvent({ name: 'Lab - Track - Completed' });
  };

  const { isChecking, isCompleted, checkChallenge, setQuizAnswer } = useCheckChallenge(
    mode,
    team,
    track,
    challenge,
    inviteID,
    userID,
    onTrackComplete,
  );
  const [showSkipModal, setShowSkipModal] = useState(false);
  const trackAmplitudeEvent = useTrackAmplitudeEvent();
  const history = useHistory();

  const nextChallenge = track.challenges.find(c => c.index === challenge.index + 1);

  const { skipToChallenge, loading: isSkipping } = useSkipToChallenge({
    teamSlug: team?.slug,
    track,
    challenge,
    nextChallenge: challenges.find(c => c.id === nextChallenge?.id)!,
    mode,
    inviteID,
    userID,
    trackReviewsEnabled: team.features?.track_reviews,
    totalChallenges: challenges.length,
  });

  const stopTrack = useCallback(async () => {
    try {
      if (!isPreview) {
        await stopTrackMutation({ variables: { trackID: track.id } });
      }
    } catch (e) {}
  }, [isPreview, stopTrackMutation, track.id]);

  const onExpire = useCallback(async () => {
    if (!isPreview) {
      setSidebarCollapsed(true);
      await stopTrack();
    }
  }, [setSidebarCollapsed, stopTrack, isPreview]);

  const toggleCollapse = useCallback(() => {
    setSidebarCollapsed(!sidebarCollapsed);
    trackAmplitudeEvent({
      name: 'Lab - Challenge - Toggled sidebar',
      data: { action: sidebarCollapsed ? 'closed' : 'opened' },
    });
  }, [setSidebarCollapsed, sidebarCollapsed, trackAmplitudeEvent]);

  const trackRefreshTab = useCallback(
    (type: ChallengeTabType) => {
      trackAmplitudeEvent({ name: 'Lab - Challenge - Refreshed tab', data: { type } });
    },
    [trackAmplitudeEvent],
  );

  const totalChallenges = track.challenges.length;

  const onNext = () => {
    if (!nextChallenge) {
      let path = `${createPathPrefix({ mode, inviteID })}/${team.slug}/tracks/${track.slug}`;

      // IN-953 Completion screen experiment
      if (team.slug === 'test-drive' && track.slug === 'ansible-tower-demo') {
        path += '/completion';
      } else if (team.features?.track_reviews) {
        path += '/recap';
      }

      history.push(path, history.location.state);
      return;
    }

    const impersonateParams = inviteID && userID ? `?userID=${userID}&inviteID=${inviteID}` : '';

    history.push(
      `${createPathPrefix({ mode, inviteID })}${nextChallenge.permalink}/notes${impersonateParams}`,
      history.location.state,
    );
  };

  const onExit = () => {
    stopTrack();

    const finishURL = inviteID ? `/${team.slug}/invite/${inviteID}` : track?.permalink;
    if (finishURL) {
      history.push(finishURL);
    }
  };

  return (
    <>
      <Helmet>
        <title>{challenge.title ? `${challenge.title} - instruqt` : 'instruqt'}</title>
        <meta name="description" content={challenge.teaser} />
      </Helmet>
      <Overflow>
        {!stopTrackCalled && track.idle_timeout && (
          <IdleTracker
            idleTimeout={secondsToMilliseconds(track.idle_timeout)}
            modalDuration={minutesToMilliseconds(1)}
            onAction={!isPreview ? keepAliveMutation : undefined}
            onExit={onExit}
            onExpire={onExpire}
          />
        )}

        <SkipModal
          open={showSkipModal}
          onClose={() => setShowSkipModal(false)}
          onSkip={!isPreview ? () => skipToChallenge(nextChallenge?.id) : undefined}
        />

        <LoadingModal open={isSkipping} message="Skipping..." />

        <Container isPreview={isPreview}>
          <AssignmentSidebar
            startResizing={() => setIsResizing(true)}
            stopResizing={() => setIsResizing(false)}
            collapsed={sidebarCollapsed}
            width={sidebarWidth}
            setWidth={setSidebarWidth}
            sidebarPosition={sidebarPosition}
            sidebarOverlay={sidebarOverlay}
          >
            <Assignment
              mode={mode}
              teamSlug={team.slug}
              track={track}
              challenges={challenges}
              challenge={challenge}
              onCheckChallenge={!isPreview ? checkChallenge : () => {}}
              isChecking={isChecking}
              toggleNotes={toggleNotes}
              collapsed={sidebarCollapsed}
              completed={isCompleted}
              hasNextChallenge={!!nextChallenge}
              onNext={!isPreview ? onNext : () => {}}
              onExpire={onExpire}
              stopTrackCalled={stopTrackCalled}
              skippingEnabled={skippingEnabled}
              onSkip={() => setShowSkipModal(true)}
              toggleCollapse={toggleCollapse}
              trackReviewsEnabled={team.features?.track_reviews}
              sidebarPosition={sidebarPosition}
              onSidebarPositionChange={setSidebarPosition}
            />
          </AssignmentSidebar>

          <WorkspaceCol sidebarPosition={sidebarPosition}>
            <Workspace
              isPreview={isPreview}
              mode={mode}
              track={track}
              challenge={challenge}
              team={team}
              onQuizAnswerChange={setQuizAnswer}
              onRefreshTab={trackRefreshTab}
              expired={stopTrackCalled}
              captureFocusOnServiceTabs={team.features?.service_tab_focus_overlay}
              isResizing={isResizing}
              sidebarCollapsed={sidebarCollapsed}
              sidebarOverlay={sidebarOverlay}
              sidebarPosition={sidebarPosition}
              sidebarWidth={sidebarWidth}
              useNewTerminal={team.features?.use_new_terminal ?? false}
            />
          </WorkspaceCol>

          {showNotes && (
            <NotesOverlay teamSlug={team.slug} notes={challenge.notes} toggleNotes={toggleNotes} invite={invite} />
          )}
        </Container>
      </Overflow>
    </>
  );
}

function useCheckChallenge(
  mode: Mode,
  team: NonNullable<LearnEnvironmentQuery['team']>,
  track: NonNullable<LearnEnvironmentQuery['track']>,
  challenge: NonNullable<LearnEnvironmentQuery['challenge']>,
  inviteID: string | null,
  userID: string | null,
  onCompleted: () => void,
) {
  const { dispatch } = useNotificationContext();
  const [quizAnswer, setQuizAnswer] = useQuiz();
  const trackAmplitudeEvent = useTrackAmplitudeEvent();
  const { sendSkilljarEmbedEvent } = useSkilljar();

  const [checkChallengeMutation, { data: challengeData, loading: isCheckingChallenge }] = useCheckChallengeMutation();
  const [checkQuizMutation, { data: quizData, loading: isCheckingQuiz }] = useCheckQuizMutation();

  const isChecking = isCheckingChallenge || isCheckingQuiz;
  const isCompleted =
    challengeData?.checkChallenge?.status === 'completed' || quizData?.checkQuiz?.status === 'completed';

  const totalChallenges = track.challenges.length;

  const logCompletedChallengeEvent = useCallback(() => {
    embedEvent('track.challenge_completed', {
      track_slug: track.slug,
      challenge_slug: challenge.slug,
      total_challenges: totalChallenges,
    });

    sendSkilljarEmbedEvent({
      challengesPassed: challenge.index + 1,
      challengesTotal: totalChallenges,
      trackFinished: challenge.index + 1 === totalChallenges,
    });
  }, [challenge.slug, totalChallenges, track.slug, sendSkilljarEmbedEvent, challenge.index]);

  const checkChallenge = useCallback(async () => {
    if (isCheckingChallenge || isCheckingQuiz) return;

    let data: CheckChallengeMutation | CheckQuizMutation | null | undefined;

    const challengeID = challenge.id;
    const type = challenge.type;

    try {
      switch (type) {
        case ChallengeType.Challenge:
          ({ data } = await checkChallengeMutation({ variables: { challengeID, inviteID, userID } }));
          break;
        case ChallengeType.Quiz:
          ({ data } = await checkQuizMutation({ variables: { challengeID, inviteID, userID, solution: quizAnswer } }));
          break;
      }
    } catch (e) {
      switch (type) {
        case ChallengeType.Challenge:
          dispatch(
            'error',
            'Failed to check the challenge',
            'Something went wrong while checking the challenge, please try again.',
            10,
          );
          break;
        case ChallengeType.Quiz:
          dispatch(
            'error',
            'Failed to check the quiz',
            'Something went wrong while checking the quiz, please try again.',
            10,
          );
          break;
      }

      return;
    }

    const check = data && 'checkChallenge' in data ? data.checkChallenge : data?.checkQuiz;

    if (check) {
      const { status, attempts } = check;

      trackAmplitudeEvent({
        name: 'Lab - Challenge - Checked',
        data: { type, status, attempts: attempts.length },
      });

      if (status === ChallengeStatus.Completed) {
        logCompletedChallengeEvent();

        if (!track.challenges.find(c => c.index === challenge.index + 1)) {
          onCompleted();
        }
      }
    }
  }, [
    challenge.id,
    challenge.type,
    checkChallengeMutation,
    checkQuizMutation,
    isCheckingChallenge,
    isCheckingQuiz,
    trackAmplitudeEvent,
    logCompletedChallengeEvent,
    dispatch,
    quizAnswer,
    inviteID,
    userID,
    track.challenges,
    challenge.index,
    onCompleted,
  ]);

  return {
    isChecking,
    isCompleted,
    setQuizAnswer,
    checkChallenge,
  };
}

function useQuiz() {
  const [state, setState] = useState<number[]>([]);

  return [
    state,
    (e: ChangeEvent<HTMLInputElement>, index: number) => {
      if (e?.target?.checked) {
        setState([...state, index]);
      } else {
        setState(state.filter(i => i !== index));
      }
    },
  ] as const;
}

gql`
  mutation CheckChallenge($challengeID: String!, $inviteID: String, $userID: String) {
    checkChallenge(challengeID: $challengeID, inviteID: $inviteID, userID: $userID) {
      id
      status
      attempts {
        timestamp
        message
      }

      unlocks {
        id
        permalink
      }
    }
  }
`;

gql`
  mutation CheckQuiz($challengeID: String!, $userID: String, $inviteID: String, $solution: [Int!]!) {
    checkQuiz(challengeID: $challengeID, userID: $userID, inviteID: $inviteID, solution: $solution) {
      id
      status
      attempts {
        timestamp
        message
      }

      unlocks {
        id
        permalink
      }
    }
  }
`;

gql`
  mutation LearnEnvironmentKeepAlive($trackId: ID!) {
    keepAlive(trackID: $trackId)
  }
`;

gql`
  query LearnEnvironment(
    $trackSlug: String!
    $challengeSlug: String!
    $teamSlug: String!
    $userID: String
    $inviteID: String
  ) {
    team(teamSlug: $teamSlug) {
      id
      slug
      name
      features {
        track_reviews
        service_tab_focus_overlay
        members_skip_to_challenge
        use_new_terminal
      }
    }

    track(trackSlug: $trackSlug, teamSlug: $teamSlug, userID: $userID) {
      id
      title
      permalink
      slug
      status
      started
      creating
      created
      failed
      stopped
      completed
      cleaned
      show_timer
      ttl
      idle_timeout
      challenges {
        id
        status
        index
        permalink
        ...ProgressChallenge
      }
      participant {
        id
        ttlExtendedByUser
      }
      lab_config {
        overlay
        position
        width
        extendTTL
      }
      ...ProgressTrack
      ...ExpiredScreenTrack
    }

    userInTeam(teamSlug: $teamSlug)

    challenge(
      trackSlug: $trackSlug
      teamSlug: $teamSlug
      challengeSlug: $challengeSlug
      userID: $userID
      inviteID: $inviteID
      parseAssignmentVariables: true
    ) {
      id
      type
      index
      teaser
      permalink
      title
      slug
      status
      assignment
      answers
      can_check
      failed

      notes {
        id
        type
        contents
        url
      }

      attempts {
        timestamp
        message
      }

      ...WorkspaceChallenge
    }

    ...IsSkippingEnabledQuery
  }

  mutation extendSandboxTTL($sandboxID: String!, $until: Time!) {
    updateSandboxTTL(sandboxID: $sandboxID, until: $until) {
      id
      track {
        id
        ttl
        participant {
          id
          ttlExtendedByUser
        }
      }
    }
  }
`;

gql`
  mutation LearnEnvironmentStopTrack($trackID: String!) {
    stopTrack(trackID: $trackID) {
      id
    }
  }
`;

gql`
  query learnEnvironmentInvite($inviteID: String!) {
    trackInvite(inviteID: $inviteID) {
      id
      canClaim
      enableLearnerSharing
      allowAnonymous
      allowedEmailAddresses
    }
  }
`;

const Overflow = tw.div`overflow-hidden`;

export const Container = styled.div(({ isPreview = false }: { isPreview?: boolean }) => [
  tw`
    flex
    box-border
    w-full
    relative
    transition-[width]
    duration-fast
    ease-in-out
  `,
  css`
    height: ${isPreview ? 'calc(100vh - 88px)' : '100vh'};
    /* Set font smoothing to antialiased, because the learn environment renders light fonts on a dark background. */
    -webkit-font-smoothing: subpixel-antialiased;
    -moz-osx-font-smoothing: auto;
  `,
]);

export const WorkspaceCol = styled.div(({ sidebarPosition = 'right' }: { sidebarPosition?: AssignmentPosition }) => [
  tw`flex grow flex-col p-0 relative h-full top-0 left-0 overflow-hidden`,
  sidebarPosition === 'right' ? tw`order-1` : tw`order-2`,
]);
